/*
 * NARBASE TECHNOLOGIES CONFIDENTIAL
 * ______________________________
 * [2017] - [2026] Narbase Technologies
 * All Rights Reserved.
 * Created by Omnia
 * On: 13/Oct/2026.
 */

package com.narbase.letsgo.web.views.ridepromocode

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.dimensions.vw
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.Observable
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.currency.CurrencyDto
import com.narbase.letsgo.web.dto.ridepromocode.AdminRidePromoCodeDto
import com.narbase.letsgo.web.dto.ridepromocode.ExtendedRidePromoCodeDto
import com.narbase.letsgo.web.dto.ridepromocode.LocationLockDto
import com.narbase.letsgo.web.dto.ridepromocode.PromoAdditionalInfoDto
import com.narbase.letsgo.web.network.remoteProcess
import com.narbase.letsgo.web.routing.domain.admin.currencies.GetCurrenciesEndpoint
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.datetime.dto
import com.narbase.letsgo.web.utils.datetime.toDate
import com.narbase.letsgo.web.utils.formatToDateInputElement
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent

class UpsertRidePromoCodeDialog(
    private val activeAutoAssignProvider: () -> Array<ExtendedRidePromoCodeDto>,
    private val onUpdated: () -> Unit
) {
    private var dto: AdminRidePromoCodeDto? = null
    private val popUp = popUpDialog { }
    private val viewModel = RidePromoCodesManagementViewModel()

    private val modes = arrayOf("Percentage", "Fixed amount")
    private var isPercentage = true
    private var hasError = false

    private val isAppOnlySelected = Observable<Boolean>()
    private val autoAssignSelected = Observable<Boolean>()

    private var errorTextView: TextView? = null
    private var warningTextView: TextView? = null
    private var percentageBox: LinearLayout? = null
    private var amountBox: LinearLayout? = null

    private var codeInput: TextInput? = null
    private var percentageInput: TextInput? = null
    private var amountInput: TextInput? = null
    private var limitInput: TextInput? = null
    private var expirationInput: TextInput? = null
    private var tripsInput: TextInput? = null
    private var maxUsersInput: TextInput? = null
    private var budgetInput: TextInput? = null
    private var currencyInput: TextInput? = null
    private var pickupLatInput: TextInput? = null
    private var pickupLngInput: TextInput? = null
    private var pickupRadiusInput: TextInput? = null
    private var destLatInput: TextInput? = null
    private var destLngInput: TextInput? = null
    private var destRadiusInput: TextInput? = null
    private var startDateInput: TextInput? = null
    private var endDateInput: TextInput? = null
    private var selectedCurrency: CurrencyDto? = null
    private var currencyDropList: RemoteDropDownList<CurrencyDto>? = null

    private var notificationTitleInput: TextInput? = null
    private var notificationTextArea: TextArea? = null

    fun add() { dto = null; selectedCurrency = null; show() }
    fun edit(item: ExtendedRidePromoCodeDto) {
        dto = item.ridePromoCode
        selectedCurrency = item.currency
        show()
    }
    private fun LinearLayout.field(
        label: String,
        required: Boolean?,
        type: String = "text",
        value: String? = null,
        hint: String? = null,
        min: String? = null,
        max: String? = null,
        allowDecimals: Boolean = true
    ): TextInput = theme.labeledTextInput(this, label.localized(), required == true) {
        element.type = type
        if (type == "number") {
            element.step = if (allowDecimals) "any" else "1"
            if (min != null) element.min = min
            if (max != null) element.max = max
            if (min?.toDoubleOrNull()?.let { it >= 0 } == true) blockNegativeInput(element, allowDecimals)
        }
        if (hint != null) element.placeholder = hint
        text = value ?: ""
    }

    private fun LinearLayout.switch(title: String, value: Boolean, selected: Observable<Boolean>) {
        val switch = ToggleSwitch()
        horizontalLayout {
            switch.resetIsSelected()
            switch.isSelected.value = value
            switch.isSelected.observe { if (it != null) selected.value = it }
            theme.label(this, title.localized(), false)
            mount(switch)
        }
    }

    private fun show() {
        hasError = false
        viewModel.setUpsertUiStateToNull()
        val d = dto
        isPercentage = d?.isPercentage ?: true

        theme.showDialog(popUp) { builder ->
            verticalLayout {
                id = "upsertRidePromoCodeRootView"
                style {
                    height = wrapContent
                    width = 50.vw
                    backgroundColor = Color.white
                    borderRadius = 8.px
                    padding = 20.px
                }
                theme.bigTextView(this) {
                    text = (if (d == null) "Add promo code" else "Edit promo code").localized()
                }
                if (d != null) {
                    theme.text(this) {
                        style {
                            color = AppColors.textVeryLightGrey
                        }
                        text = "Used by ${d.actualUsersCount} / ${d.maxUsersCount} users, spent ${d.spending} of ${d.budget}. Editing does not reset these counters."
                    }
                }
                verticalFiller(8)

                codeInput = field("Code", true, value = d?.code)

                verticalFiller(8)
                theme.label(this, "Discount type".localized(), true)
                setupDropDownList(
                    name = "",
                    list = modes,
                    itemToString = { it },
                    defaultValue = if (isPercentage) modes[0] else modes[1],
                    onItemSelected = {
                        isPercentage = it == modes[0]
                        applyMode()
                    },
                )
                percentageBox = verticalLayout {
                    style { width = matchParent }
                    percentageInput = field("Percentage (1-100)", true, "number",
                            d?.percentage?.takeIf { d.isPercentage }?.toString(), min = "0", max = "100")                }
                amountBox = verticalLayout {
                    style { width = matchParent }
                    amountInput = field("Amount", true, "number", d?.amount?.toString(), min = "0")
                }
                applyMode()

                limitInput = field("Limit per trip", true, "number", d?.limitPerTrip?.toString(), min = "0")
                expirationInput = field(
                    "Expiration date", true, "date",
                    d?.expiration?.toDate()?.formatToDateInputElement() ?: ""
                    ?: ""
                )
                tripsInput = field("Trips per user", true, "number", d?.tripsPerUser?.toString(), min = "1", allowDecimals = false)
                maxUsersInput = field("Max users", true, "number", d?.maxUsersCount?.toString(), min = "1", allowDecimals = false)
                budgetInput = field("Budget", true, "number", d?.budget?.toString(), min = "0")
                currenciesList()
                verticalFiller(8)
                theme.label(this, "Pickup lock (optional, all three or none)".localized(), false)
                pickupLatInput = field("Pickup latitude", null, "number", d?.pickupLock?.lat?.toString())
                pickupLngInput = field("Pickup longitude", null, "number", d?.pickupLock?.lng?.toString())
                pickupRadiusInput = field("Pickup radius (km)", null, "number", d?.pickupLock?.radiusInKm?.toString(), min = "0")

                theme.label(this, "Destination lock (optional, all three or none)".localized(), false)
                destLatInput = field("Destination latitude", null, "number", d?.destLock?.lat?.toString())
                destLngInput = field("Destination longitude", null, "number", d?.destLock?.lng?.toString())
                destRadiusInput = field("Destination radius (km)", null, "number", d?.destLock?.radiusInKm?.toString(), min = "0")

                startDateInput = field(
                    "Start date lock", false, "date",
                    d?.startDateLock?.toDate()?.formatToDateInputElement()
                )
                endDateInput = field(
                    "End date lock", false, "date",
                    d?.endDateLock?.toDate()?.formatToDateInputElement()
                )

                verticalFiller(8)
                switch("App only", d?.isAppOnly ?: false, isAppOnlySelected)
                switch("Auto-assign on registration", d?.autoAssignOnRegistration ?: false, autoAssignSelected)

                notificationOnPromoAssigned(d)
                verticalFiller(8)
                warningTextView = textView {
                    style {
                        fontSize = 13.px
                        color = AppColors.redDark
                        marginBottom = 8.px
                    }
                    isVisible = false
                }
                autoAssignSelected.observe { updateWarning() }
                updateWarning()

                errorTextView = textView {
                    style {
                        marginBottom = 8.px
                        fontSize = 14.px
                        color = AppColors.redLight
                    }
                    isVisible = false
                }

                builder.bottomBar = {
                    horizontalLayout {
                        style {
                            width = matchParent
                            height = wrapContent
                            justifyContent = JustifyContent.End
                        }
                        theme.unimportantButton(this) {
                            text = "Dismiss".localized()
                            onClick = { popUp.dismissDialog() }
                        }
                        horizontalFiller(16)
                        viewModel.upsertUiState.clearObservers()
                        theme.mainButton(this) {
                            text = "Save".localized()
                            id = "SaveRidePromoCodeButton"
                            onClick = { onSave() }
                        }.withLoadingAndError(
                            viewModel.upsertUiState,
                            onRetryClicked = { onSave() },
                            onLoaded = {
                                popUp.dismissDialog()
                                onUpdated()
                            }
                        )
                    }
                }
            }
        }
    }

    fun LinearLayout.notificationOnPromoAssigned( d: AdminRidePromoCodeDto?) = verticalLayout {
        verticalFiller(8)
        matchParentWidth()

        theme.label(
            this,
            "Additional notification (optional)".localized(),
            false
        )

        notificationTitleInput = field(
            "Notification title".localized(),
            false,
            value = d?.promoAdditionalInfo?.notificationTitle
        )

        notificationTextArea = theme.labeledTextArea(this,"Notification text".localized()) {
            text = d?.promoAdditionalInfo?.notificationBody ?: ""
        }
    }

    private fun View.currenciesList() {
        verticalLayout {
            matchParentWidth()
            theme.label(this, "Currency".localized(), isRequired = true)
            currencyDropList = setupDropDownList(
                "Currency".localized(),
                getList = { pageNo, pageSize, searchTerm ->
                    try {
                        GetCurrenciesEndpoint.remoteProcess(
                            GetCurrenciesEndpoint.Request(pageNo, pageSize, searchTerm)
                        ).data.list
                    } catch (t: Throwable) {
                        t.printStackTrace()
                        arrayOf()
                    }
                },
                itemToString = { it.name },
                onItemSelected = { selectedCurrency = it },
                defaultItem = selectedCurrency,
                viewWidthFactory = { matchParent },
                showAutoComplete = true
            )
        }
    }

    private fun applyMode() {
        percentageBox?.isVisible = isPercentage
        amountBox?.isVisible = !isPercentage
    }

    private fun updateWarning() {
        val ownId = dto?.id?.toString()
        val others = activeAutoAssignProvider().filter { it.ridePromoCode.id?.toString() != ownId }
        val isOn = autoAssignSelected.value == true
        warningTextView?.isVisible = isOn && others.isNotEmpty()
        if (isOn && others.isNotEmpty()) {
            warningTextView?.text =
                ("Other auto-assign promo codes are already active: " + others.joinToString { it.ridePromoCode.code } +
                        ". New passengers will redeem all of them, but only the newest one will be their active promo.").localized()
        }
    }

    private fun fail(message: String) {
        hasError = true
        errorTextView?.text = message.localized()
        errorTextView?.isVisible = true
    }

    private fun readLock(
        name: String, lat: TextInput?, lng: TextInput?, radius: TextInput?
    ): LocationLockDto? {
        val texts = listOf(lat, lng, radius).map { it?.text?.trim().orEmpty() }
        if (texts.all { it.isBlank() }) return null
        val la = texts[0].toDoubleOrNull()
        val lo = texts[1].toDoubleOrNull()
        val ra = texts[2].toDoubleOrNull()
        if (la == null || lo == null || ra == null || ra <= 0.0) {
            fail("$name lock needs a valid latitude, longitude and a radius greater than 0")
            return null
        }
        return LocationLockDto(la, lo, ra)
    }

    private fun onSave() {
        hasError = false
        errorTextView?.isVisible = false

        val code = codeInput?.text?.trim().orEmpty()
        if (code.isBlank()) return fail("Code is required")
        val percentage = percentageInput?.text?.toDoubleOrNull()
        val amount = amountInput?.text?.toDoubleOrNull()
        if (isPercentage && (percentage == null || percentage <= 0.0 || percentage > 100.0))
            return fail("Percentage must be between 0 and 100")
        if (!isPercentage && (amount == null || amount <= 0.0))
            return fail("Amount must be greater than 0")
        val limit = limitInput?.text?.toDoubleOrNull()?.takeIf { it >= 0 }
            ?: return fail("Limit per trip must be 0 or more")
        val expiration = expirationInput?.text?.trim()?.takeIf { it.isNotBlank() }?.toDate()?.dto() ?: return fail("Expiration date is required")
        val trips = tripsInput?.text?.toIntOrNull()?.takeIf { it >= 1 } ?: return fail("Trips per user must be at least 1")
        val maxUsers = maxUsersInput?.text?.toIntOrNull()?.takeIf { it >= 1 } ?: return fail("Max users must be at least 1")
        val budget = budgetInput?.text?.toDoubleOrNull()?.takeIf { it >= 0 } ?: return fail("Budget is required")
        val currencyId = selectedCurrency?.id

        val start = startDateInput?.text?.trim()?.takeIf { it.isNotBlank() }?.toDate()?.dto()
        val end = endDateInput?.text?.trim()?.takeIf { it.isNotBlank() }?.toDate()?.dto()
        if (start != null && end != null && start.milliSeconds > end.milliSeconds)
            return fail("Start date lock must not be after end date lock")

        val pickupLock = readLock("Pickup", pickupLatInput, pickupLngInput, pickupRadiusInput)
        if (hasError) return
        val destLock = readLock("Destination", destLatInput, destLngInput, destRadiusInput)
        if (hasError) return
        val notificationTitle =
            notificationTitleInput?.text?.trim().orEmpty()

        val notificationText =
            notificationTextArea?.text?.trim().orEmpty()

        val promoAdditionalInfo =
            if (notificationTitle.isBlank() && notificationText.isBlank()) {
                null
            } else {
                PromoAdditionalInfoDto(
                    notificationTitle = notificationTitle,
                    notificationBody = notificationText
                )
            }

        viewModel.upsert(
            AdminRidePromoCodeDto(
                id = dto?.id,
                code = code,
                isPercentage = isPercentage,
                percentage = if (isPercentage) percentage else null,
                amount = if (isPercentage) null else amount,
                limitPerTrip = limit,
                expiration = expiration,
                tripsPerUser = trips,
                maxUsersCount = maxUsers,
                actualUsersCount = dto?.actualUsersCount ?: 0,   // ignored by the server
                budget = budget,
                spending = dto?.spending ?: 0.0,                 // ignored by the server
                pickupLock = pickupLock,
                destLock = destLock,
                currencyId = currencyId,
                startDateLock = start,
                endDateLock = end,
                isAppOnly = isAppOnlySelected.value ?: dto?.isAppOnly ?: false,
                autoAssignOnRegistration = autoAssignSelected.value ?: dto?.autoAssignOnRegistration ?: false,
                promoAdditionalInfo = promoAdditionalInfo
            ),
            onError = { name ->
                fail(name)
            }
        )
    }

    private fun blockNegativeInput(input: HTMLInputElement, allowDecimals: Boolean) {
        val blockedKeys =
            if (allowDecimals) setOf("-", "+", "e", "E")
            else setOf("-", "+", "e", "E", ".", ",")
        input.addEventListener("keydown", { event ->
            if ((event as KeyboardEvent).key in blockedKeys) event.preventDefault()
        })
        input.addEventListener("input", {
            val cleaned = input.value.replace("-", "")
            if (cleaned != input.value) input.value = cleaned
        })
    }
}
